/**
 * Sentry Utility Functions
 * Helper functions for manual error capture, context setting, and breadcrumbs
 *
 * These utilities provide a consistent interface for Sentry operations across the codebase.
 * All functions include error handling guards to prevent Sentry failures from crashing the app.
 */

import * as Sentry from '@sentry/react';
import { browserTracingIntegration, replayIntegration } from '@sentry/react';
import { ERROR_LEVELS } from '../../constants/sentry-constants';

/**
 * Export Sentry integrations for use in initialization
 */
export { browserTracingIntegration, replayIntegration };

/**
 * Sentry User interface
 */
export interface SentryUser {
  id?: string;
  email?: string;
  username?: string;
  accountId?: string;
  accountName?: string;
  [key: string]: unknown;
}

/**
 * Check if Sentry is initialized
 */
export function isSentryInitialized(): boolean {
  try {
    return Sentry.getClient() !== undefined;
  } catch {
    return false;
  }
}

/**
 * Check if Sentry warnings should be shown
 */
function shouldWarnAboutSentry(): boolean {
  if (!import.meta.env.DEV) {
    return false;
  }

  const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
  const sentryEnabled = import.meta.env.VITE_SENTRY_ENABLED !== 'false';

  return sentryEnabled && !!sentryDsn;
}

/**
 * Manually capture an exception
 */
export function captureException(
  error: Error | string,
  context?: {
    tags?: Record<string, string>;
    extra?: Record<string, unknown>;
    level?: Sentry.SeverityLevel;
    contexts?: Record<string, Record<string, unknown>>;
  }
): void {
  if (!isSentryInitialized()) {
    if (shouldWarnAboutSentry()) {
      console.warn('[Sentry] Cannot capture exception - Sentry not initialized');
    }
    return;
  }

  try {
    const errorObj = typeof error === 'string' ? new Error(error) : error;

    Sentry.captureException(errorObj, {
      tags: context?.tags,
      extra: context?.extra,
      level: context?.level || ERROR_LEVELS.ERROR,
      contexts: context?.contexts,
    });
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn('[Sentry] Failed to capture exception:', err);
    }
  }
}

/**
 * Manually capture a message
 */
export function captureMessage(
  message: string,
  level: Sentry.SeverityLevel = ERROR_LEVELS.INFO,
  context?: {
    tags?: Record<string, string>;
    extra?: Record<string, unknown>;
  }
): void {
  if (!isSentryInitialized()) {
    if (shouldWarnAboutSentry()) {
      console.warn('[Sentry] Cannot capture message - Sentry not initialized');
    }
    return;
  }

  try {
    Sentry.captureMessage(message, {
      level,
      tags: context?.tags,
      extra: context?.extra,
    });
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn('[Sentry] Failed to capture message:', err);
    }
  }
}

/**
 * Add a breadcrumb
 */
export function addBreadcrumb(breadcrumb: {
  message?: string;
  category?: string;
  type?: string;
  level?: Sentry.SeverityLevel;
  data?: Record<string, unknown>;
  timestamp?: number;
}): void {
  if (!breadcrumb.message && !breadcrumb.category) {
    if (import.meta.env.DEV) {
      console.warn('[Sentry] Breadcrumb requires message or category');
    }
    return;
  }

  if (breadcrumb.level) {
    const validLevels: Sentry.SeverityLevel[] = ['fatal', 'error', 'warning', 'info', 'debug', 'log'];
    if (!validLevels.includes(breadcrumb.level)) {
      if (import.meta.env.DEV) {
        console.warn('[Sentry] Invalid breadcrumb level:', breadcrumb.level);
      }
      return;
    }
  }

  if (!isSentryInitialized()) {
    if (shouldWarnAboutSentry()) {
      console.warn('[Sentry] Cannot add breadcrumb - Sentry not initialized');
    }
    return;
  }

  try {
    Sentry.addBreadcrumb({
      ...breadcrumb,
      timestamp: breadcrumb.timestamp || Date.now() / 1000,
    });
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn('[Sentry] Failed to add breadcrumb:', err);
    }
  }
}

/**
 * Set custom context
 */
export function setContext(key: string, context: Record<string, unknown>): void {
  if (!isSentryInitialized()) {
    if (shouldWarnAboutSentry()) {
      console.warn('[Sentry] Cannot set context - Sentry not initialized');
    }
    return;
  }

  try {
    Sentry.setContext(key, context);
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn('[Sentry] Failed to set context:', err);
    }
  }
}

/**
 * Set custom tag
 */
export function setTag(key: string, value: string): void {
  if (!isSentryInitialized()) {
    if (shouldWarnAboutSentry()) {
      console.warn('[Sentry] Cannot set tag - Sentry not initialized');
    }
    return;
  }

  try {
    Sentry.setTag(key, value);
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn('[Sentry] Failed to set tag:', err);
    }
  }
}

/**
 * Set user context in Sentry
 */
export function setUser(user: SentryUser | null): void {
  if (!isSentryInitialized()) {
    if (shouldWarnAboutSentry()) {
      console.warn('[Sentry] Cannot set user - Sentry not initialized');
    }
    return;
  }

  try {
    Sentry.setUser(user);
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn('[Sentry] Failed to set user:', err);
    }
  }
}
